import { type ChangeEvent, useId, useRef, useState } from "react"
import { useTranslation } from "react-i18next"

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"

/**
 * OKS-40 — Brand logo upload widget.
 *
 * Displays the current logo (or initials placeholder), accepts a
 * new file via `<input type="file" accept="image/*">`, and shows a
 * client-side preview of the new file before save. The actual upload
 * happens via the parent Inertia `<Form>` submit (multipart triggers
 * automatically when a file input is present in the form).
 *
 * Read-only mode (Brand Member tier): the file input is removed from
 * the DOM and the avatar shows the current logo without affordances.
 */
export interface BrandLogoUploaderProps {
  /** Existing logo URL from the Inertia prop (`brand.logo_url`). */
  currentUrl: string | null
  /** Two-character fallback (initials of brand name) for the placeholder. */
  fallback: string
  /** Wire format: `<input name="brand[logo]">`. */
  name?: string
  /** Disable affordances for member-tier reads. */
  readOnly?: boolean
  /** Test id wrapper. */
  testId?: string
}

export function BrandLogoUploader({
  currentUrl,
  fallback,
  name = "brand[logo]",
  readOnly = false,
  testId = "brand-logo-uploader",
}: BrandLogoUploaderProps) {
  const { t } = useTranslation()
  const inputRef = useRef<HTMLInputElement>(null)
  const inputId = useId()
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)

  const onPick = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) {
      setPreviewUrl(null)
      return
    }
    setPreviewUrl(URL.createObjectURL(file))
  }

  const onClear = () => {
    setPreviewUrl(null)
    if (inputRef.current) inputRef.current.value = ""
  }

  const displayUrl = previewUrl ?? currentUrl

  return (
    <div data-testid={testId} className="flex items-start gap-4">
      <Avatar className="h-20 w-20 rounded-md">
        {displayUrl ? (
          <AvatarImage
            src={displayUrl}
            alt=""
            data-testid={`${testId}-image`}
            className="rounded-md"
          />
        ) : null}
        <AvatarFallback
          data-testid={`${testId}-fallback`}
          className="rounded-md"
        >
          {fallback}
        </AvatarFallback>
      </Avatar>

      {readOnly ? null : (
        <div className="flex flex-1 flex-col gap-2">
          <Label htmlFor={inputId}>
            {t("settings.brand.profile.logo_label")}
          </Label>
          <Input
            ref={inputRef}
            id={inputId}
            type="file"
            name={name}
            accept="image/jpeg,image/png,image/webp"
            onChange={onPick}
            data-testid={`${testId}-input`}
          />
          <p className="text-muted-foreground text-xs">
            {t("settings.brand.profile.logo_helper")}
          </p>
          {previewUrl ? (
            <Button
              type="button"
              variant="link"
              size="sm"
              onClick={onClear}
              data-testid={`${testId}-clear`}
              className="h-auto p-0 text-xs"
            >
              {t("settings.brand.profile.logo_clear")}
            </Button>
          ) : null}
        </div>
      )}
    </div>
  )
}

export default BrandLogoUploader
